import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { ExpiredPointsComponent } from '../expired-points/expired-points.component';
import {
  PointLeaderboardService,
  EventPerformance,
  EventCategoryBreakdownItem,
  EditSidebarService,
  LeaderboardMode,
  CombinedEventBreakdownEvent,
  CombinedEventBreakdownCategory,
  CombinedEventBreakdownMatch,
  EventMatchBreakdownResponse,
  ComponentSummaryV2Response,
  ComponentSummaryV2Event,
  ComponentSummaryV2Match,
} from '../../../utilities/services/point-leaderboard/point-leaderboard.service';
import { DialogModule } from 'primeng/dialog';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonLoaderComponent } from '../../shared/skeleton-loader/skeleton-loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';


interface ComponentSummary {
  heading: string;
  points: number;
}

interface BreakdownPointsSummary {
  eloPoints: number | null;
  bonusPoints: number | null;
  totalPoints: number | null;
}

/** Row rendered inside the Figma-style category point breakdown card. */
interface PointBreakdownRow {
  translationKey: string;
  value: number;
  tournamentWeight: string | null;
  clickable: boolean;
  eventBreakdown?: Record<string, unknown>;
  stageId?: number;
  stageName?: string;
}

/** Cached bulk component-summary payload item for a category drill-down. */
interface CategorySummaryItem {
  ranking_list_id: string;
  category_id: number;
  category_name: string;
  calculation_basis?: string | null;
  events_breakdown?: Array<Record<string, unknown>>;
}

type AccordionIndex = number | number[] | null;

type CombinedMatchesSortBy =
  | 'status'
  | 'opponent_name'
  | 'date'
  | 'score'
  | 'exchange';

@Component({
  selector: 'stupa-point-breakdown',
  standalone: true,
  imports: [
    MatTooltipModule,
    CommonModule,
    ExpiredPointsComponent,
    DialogModule,
    AccordionModule,
    TooltipModule,
    SkeletonLoaderComponent,
    TranslateModule
  ],
  templateUrl: './point-breakdown.component.html',
  styleUrl: './point-breakdown.component.scss',
  animations: [
    trigger('breakdownAnimation', [
      state('hidden', style({ opacity: 0, transform: 'translateY(-10px)' })),
      state('visible', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('hidden => visible', animate('300ms ease-in-out')),
      transition('visible => hidden', animate('300ms ease-in-out')),
    ]),
  ],
})
export class PointBreakdownComponent implements OnChanges {

  constructor(
    private readonly leaderboardService: PointLeaderboardService,
    private readonly editSidebarService: EditSidebarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar
  ) { }

  @Input() participantId: number | null = null;
  @Input() categoryId: number | null = null;
  @Output() panelClose = new EventEmitter<void>();
  @Output() pauseStatusChanged = new EventEmitter<void>();
  @Input() selectedBreakdownPoints: BreakdownPointsSummary | null = null;

  participantName: string = 'Player Name';
  eventPerformances: EventPerformance[] = [];
  selectedBreakdownEventId: number | null = null;
  /** Index of the MERGED parent row whose nested merged_events panel is open. */
  selectedMergedParentIndex: number | null = null;
  showExpiredPoints = false;
  isLoading: boolean = false;
  stageLoading: boolean = false;
  breakdownDetails: { [key: string]: number } | null = null;
  mappedBreakdownDetails: ComponentSummary[] = [];
  isEditSidebarOpen: boolean = false;
  selectedPointKey: string | null = null;
  showPausePopup: boolean = false;
  isPaused: boolean = false;
  current_ranking_list_id: string[] = [];
  current_participant_id: number = 0;
  breakdownDetailsList: CategorySummaryItem[] = [];
  /** Cached component-summary responses keyed by `${eventId}_${categoryId}`. */
  categorySummaryCache = new Map<string, CategorySummaryItem[]>();
  /** Tracks which category accordion is currently loading its summary. */
  loadingCategoryKey: string | null = null;
  /** Single expanded category row key inside an event breakdown panel. */
  selectedExpandedCategoryId: string | null = null;
  /** Event id currently loading flat (no-category) breakdown details. */
  breakdownEventLoadingId: number | null = null;
  current_selected_category_id: number | null = null;
  @Input() currentParticipantName: any = null;
  openAccordionTabs: Set<any> = new Set(); // Track which accordion tabs are open
  openEventAccordions: Set<number> = new Set(); // Track which event accordions are open
  isManuallyToggling: boolean = false; // Flag to prevent conflicts during manual toggle
  matches_list: any[] = [];
  showMatchesView: boolean = false; // Track if matches view is shown
  currentStageName: string = ''; // Store current stage name for back button context
  activeEventIndex: number | null = null; // Track active accordion index

  matchesViewMode: 'Ranking' | 'RankingAndRating' = 'Ranking';
  combinedEvents: CombinedEventBreakdownEvent[] = [];
  activeCombinedEventIndex: number | null = null;
  combined_matches_list: CombinedEventBreakdownMatch[] = [];
  combinedCurrentEventName: string = '';
  combinedCurrentCategoryName: string = '';
  combinedCurrentEvent: CombinedEventBreakdownEvent | null = null;
  combinedTotalPoints: number | null = null;
  combinedMatchEventDate: string | null = null;
  combinedMatchRatingInitial: number | null = null;
  combinedMatchRatingDelta: number | null = null;
  combinedMatchRatingFinal: number | null = null;
  combinedMatchBonusPoints: number | null = null;
  combinedMatchesSortBy: CombinedMatchesSortBy = 'date';
  combinedMatchesSortOrder: 'asc' | 'desc' = 'desc';
  objectKeys(obj: { [key: string]: any } | null): string[] {
    return obj ? Object.keys(obj) : [];
  }
  leaderboardMode: LeaderboardMode = 'None';
  combinedBonusPoints: number | null = null;
  combinedElloPoints: number | null = null;
  combinedOverallTotalPoints: number | null = null;

  /**
   * Whether an event-performance row represents a merged team aggregate.
   * @param event - Event row from the event-performance response.
   */
  isMergedEvent(event: EventPerformance): boolean {
    return event.event_type === 'MERGED' || event.is_merged_team_event === true;
  }

  /**
   * Returns child events for a MERGED aggregate row.
   * @param event - MERGED parent event row.
   */
  getMergedEvents(event: EventPerformance): EventPerformance[] {
    return Array.isArray(event.merged_events) ? event.merged_events : [];
  }

  /**
   * Stable track id for event rows (MERGED parents may have null event_id).
   */
  getEventTrackId(event: EventPerformance, index: number): string | number {
    return event.event_id ?? `merged-${index}`;
  }

  /**
   * Resolves display points for an event row.
   */
  getEventDisplayPoints(event: EventPerformance): number {
    return event.total_points_from_event ?? event.points ?? 0;
  }

  /**
   * Whether the MERGED parent breakdown panel is expanded.
   */
  isMergedParentBreakdownExpanded(eventIndex: number): boolean {
    return this.selectedMergedParentIndex === eventIndex;
  }

  /**
   * Whether a nested merged child event breakdown is expanded.
   */
  isMergedSubEventBreakdownExpanded(mergedEvent: EventPerformance): boolean {
    return this.selectedBreakdownEventId === mergedEvent.event_id;
  }

  /**
   * Returns category_breakdown entries for an event-performance row.
   * MERGED parent rows render merged_events instead of category_breakdown.
   * @param event - Event row from the initial event-performance response.
   */
  getCategoryBreakdown(event: EventPerformance): EventCategoryBreakdownItem[] {
    if (this.isMergedEvent(event)) {
      return [];
    }
    return Array.isArray(event.category_breakdown) ? event.category_breakdown : [];
  }

  /**
   * Builds a stable cache key for a category summary under an event.
   */
  getCategoryCacheKey(eventId: number, categoryId: number): string {
    return `${eventId}_${categoryId}`;
  }

  /**
   * Unique key for a category row within an event breakdown list.
   */
  getCategoryExpansionKey(
    eventId: number | null,
    category: EventCategoryBreakdownItem,
  ): string {
    return `${eventId ?? 'unknown'}_${category.category_id}_${category.participant_type_id}`;
  }

  /**
   * Whether a category accordion row is expanded.
   */
  isCategoryBreakdownExpanded(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): boolean {
    return (
      this.selectedExpandedCategoryId ===
      this.getCategoryExpansionKey(event.event_id, category)
    );
  }

  /**
   * Resolves optional category level label from API payload.
   */
  getCategoryLevelName(category: EventCategoryBreakdownItem): string {
    return (category.category_level_name || category.category_level || '').trim();
  }

  /**
   * Resolves participant-type chip label for category header row.
   */
  getParticipantTypeChip(category: EventCategoryBreakdownItem): string {
    if (category.participant_type_name?.trim()) {
      return category.participant_type_name.trim();
    }
    const typeMap: Record<number, string> = {
      1: 'Singles',
      2: 'Teams',
      3: 'Doubles',
      4: 'Mix-Doubles',
    };
    return typeMap[category.participant_type_id] ?? '';
  }

  /**
   * Resolves ranking list id already present on the category payload.
   */
  private resolveRankingListId(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): string | null {
    if (category.ranking_list_id) {
      return category.ranking_list_id;
    }
    if (category.ranking_list_ids?.[0]) {
      return category.ranking_list_ids[0];
    }
    return null;
  }

  /**
   * Resolves ranking list id from category payload or fetches it by category and event.
   */
  private resolveRankingListIdForCategory(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): Observable<string | null> {
    const directId = this.resolveRankingListId(event, category);
    if (directId) {
      return of(directId);
    }

    return this.leaderboardService
      .getRankingListsByCategoryAndEvent(category.category_id, event.event_id!)
      .pipe(map((response) => response.items[0]?.ranking_list_id ?? null));
  }

  /**
   * Whether flat breakdown is loading for a specific event (no categories path).
   */
  isEventBreakdownLoading(eventId: number): boolean {
    return this.breakdownEventLoadingId === eventId;
  }

  /**
   * Toggles animated breakdown panel below an event row (points / pencil click).
   */
  toggleBreakdown(
    event: EventPerformance,
    clickEvent?: Event,
    eventIndex?: number,
  ): void {
    clickEvent?.stopPropagation();

    if (this.isMergedEvent(event)) {
      const parentIndex =
        eventIndex ?? this.eventPerformances.indexOf(event);

      if (
        parentIndex >= 0 &&
        this.selectedMergedParentIndex === parentIndex
      ) {
        this.selectedMergedParentIndex = null;
        this.selectedBreakdownEventId = null;
        this.selectedExpandedCategoryId = null;
        this.breakdownDetails = null;
        this.mappedBreakdownDetails = [];
        this.breakdownEventLoadingId = null;
        this.isEditSidebarOpen = false;
        this.selectedPointKey = null;
        this.cdr.detectChanges();
        return;
      }

      this.selectedMergedParentIndex = parentIndex >= 0 ? parentIndex : null;
      this.selectedBreakdownEventId = null;
      this.selectedExpandedCategoryId = null;
      this.breakdownDetails = null;
      this.mappedBreakdownDetails = [];
      this.breakdownEventLoadingId = null;
      this.cdr.detectChanges();
      return;
    }

    if (this.selectedBreakdownEventId === event.event_id) {
      this.selectedBreakdownEventId = null;
      this.selectedMergedParentIndex = null;
      this.selectedExpandedCategoryId = null;
      this.breakdownDetails = null;
      this.mappedBreakdownDetails = [];
      this.breakdownEventLoadingId = null;
      this.isEditSidebarOpen = false;
      this.selectedPointKey = null;
      this.cdr.detectChanges();
      return;
    }

    this.selectedBreakdownEventId = event.event_id;
    this.selectedMergedParentIndex = null;
    this.selectedExpandedCategoryId = null;
    this.breakdownDetails = null;
    this.mappedBreakdownDetails = [];

    if (event.event_id !== null && this.getCategoryBreakdown(event).length === 0) {
      this.fetchBreakdownDetails(event.event_id);
    }

    this.cdr.detectChanges();
  }

  /**
   * Toggles breakdown for a child event nested under a MERGED parent row.
   */
  toggleMergedSubEventBreakdown(
    mergedEvent: EventPerformance,
    clickEvent?: Event,
  ): void {
    clickEvent?.stopPropagation();

    if (this.selectedBreakdownEventId === mergedEvent.event_id) {
      this.selectedBreakdownEventId = null;
      this.selectedExpandedCategoryId = null;
      this.breakdownDetails = null;
      this.mappedBreakdownDetails = [];
      this.breakdownEventLoadingId = null;
      this.cdr.detectChanges();
      return;
    }

    this.selectedBreakdownEventId = mergedEvent.event_id;
    this.selectedExpandedCategoryId = null;
    this.breakdownDetails = null;
    this.mappedBreakdownDetails = [];

    if (
      mergedEvent.event_id !== null &&
      this.getCategoryBreakdown(mergedEvent).length === 0
    ) {
      this.fetchBreakdownDetails(mergedEvent.event_id);
    }

    this.cdr.detectChanges();
  }

  /**
   * Expands/collapses a category row and lazy-loads point components.
   */
  onCategoryBreakdownClick(
    clickEvent: Event,
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): void {
    clickEvent.stopPropagation();
    const expansionKey = this.getCategoryExpansionKey(event.event_id, category);

    if (this.selectedExpandedCategoryId === expansionKey) {
      this.selectedExpandedCategoryId = null;
      this.cdr.detectChanges();
      return;
    }

    this.selectedExpandedCategoryId = expansionKey;
    this.loadCategoryBreakdown(event, category);
    this.cdr.detectChanges();
  }

  /**
   * Returns point rows for expanded category accordion body.
   */
  getExpandedCategoryPointRows(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): PointBreakdownRow[] {
    const summaryItems = this.getCategorySummaryItems(
      event.event_id,
      category.category_id,
    );
    if (summaryItems.length === 0) {
      return [];
    }
    return this.getCategoryPointRows(summaryItems[0], category.config_type);
  }

  /**
   * Returns tournament weight label for expanded category panel.
   */
  getExpandedCategoryTournamentWeight(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): string | null {
    const summaryItems = this.getCategorySummaryItems(
      event.event_id,
      category.category_id,
    );
    if (summaryItems.length === 0) {
      return null;
    }
    const eventBreakdown = summaryItems[0].events_breakdown?.[0];
    if (!eventBreakdown || typeof eventBreakdown !== 'object') {
      return null;
    }
    return this.extractTournamentWeight(eventBreakdown);
  }

  /**
   * Template helper for ranking list id resolution.
   */
  resolveRankingListIdForTemplate(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): string | null {
    return this.resolveRankingListId(event, category);
  }

  /**
   * Whether a category summary request is in flight.
   */
  isCategorySummaryLoading(eventId: number | null, categoryId: number): boolean {
    if (eventId === null) {
      return false;
    }
    return this.loadingCategoryKey === this.getCategoryCacheKey(eventId, categoryId);
  }

  /**
   * Returns cached component-summary items for a category, if loaded.
   */
  getCategorySummaryItems(eventId: number | null, categoryId: number): CategorySummaryItem[] {
    if (eventId === null) {
      return [];
    }
    return this.categorySummaryCache.get(this.getCategoryCacheKey(eventId, categoryId)) ?? [];
  }

  /**
   * Detects award-standing calculation from config_type or calculation_basis.
   */
  isAwardStandingConfig(configType: string | null | undefined): boolean {
    const normalized = (configType ?? '').trim().toLowerCase();
    return normalized === 'awardstanding' || normalized === 'award_and_standing';
  }

  /**
   * Persists edit-sidebar context before opening drill-down views.
   */
  private applyBreakdownContext(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
    rankingListId: string,
  ): void {
    if (event.event_id === null) {
      return;
    }

    const seasonId =
      globalThis.window !== undefined && localStorage.getItem('seasonId')
        ? +localStorage.getItem('seasonId')!
        : null;

    localStorage.setItem('Ranking_list_id', rankingListId);
    localStorage.setItem('participant_id', String(this.current_participant_id));
    sessionStorage.setItem('event_id', String(event.event_id));
    localStorage.setItem(
      'rankingManagement',
      JSON.stringify([
        {
          event_id: event.event_id,
          category_id: category.category_id,
          season_id: seasonId,
        },
      ]),
    );

    const configType = (category.config_type ?? '').trim();
    localStorage.setItem(
      'configType',
      this.isAwardStandingConfig(configType) ? 'awardStanding' : configType || 'stages',
    );
  }

  /**
   * Maps a v2 match row into the legacy matches_breakdown shape for match history UI.
   */
  private mapV2MatchToLegacyMatch(match: ComponentSummaryV2Match): Record<string, unknown> {
    const victoryPoints = match.breakdown?.victory_points ?? match.points ?? 0;
    const bonusPoints = match.breakdown?.bonus_points ?? 0;

    return {
      match_id: match.match_id,
      opponent_name: match.opponent_name,
      match_score: match.score,
      match_status: match.result,
      match_total_points_from_this_stage: match.points,
      match_point_components: {
        victory_points_plugin: { value: victoryPoints },
        stage_knockout_win: { value: bonusPoints },
      },
    };
  }

  /**
   * Normalises v2 component-summary response into cached CategorySummaryItem shape.
   */
  private mapV2ResponseToCategorySummaryItem(
    response: ComponentSummaryV2Response,
    eventId: number,
    category: EventCategoryBreakdownItem,
  ): CategorySummaryItem {
    const eventData: ComponentSummaryV2Event | undefined =
      response.events.find((item) => item.event_id === eventId) ?? response.events[0];

    if (!eventData) {
      return {
        ranking_list_id: response.ranking_list_id,
        category_id: category.category_id,
        category_name: category.category_name,
        events_breakdown: [],
      };
    }

    const eventPointSummary = {
      position_points: eventData.event_wide?.position_points ?? 0,
      victory_points: eventData.event_wide?.victory_points ?? 0,
      bonus_points: eventData.event_wide?.bonus_points ?? 0,
      penalty_points: eventData.event_wide?.penalty_points ?? 0,
      match_outcome_points: eventData.event_wide?.match_outcome_points ?? 0,
      expiry_deduction_points: eventData.event_wide?.expiry_deduction_points ?? 0,
    };

    const stages = eventData.stages.map((stage) => ({
      stage_id: stage.stage_id,
      stage_name: stage.stage_name,
      stage_total_points: stage.stage_total_points,
      tournament_weight: stage.tournament_weight,
      victory_points: stage.victory_points,
      bonus_points: stage.bonus_points,
      position_points: stage.position_points,
      penalty_points: stage.penalty_points,
      matches_breakdown: stage.matches.map((match) => this.mapV2MatchToLegacyMatch(match)),
    }));

    return {
      ranking_list_id: response.ranking_list_id,
      category_id: category.category_id,
      category_name: category.category_name,
      calculation_basis: category.config_type,
      events_breakdown: [
        {
          event_id: eventData.event_id,
          event_name: eventData.event_name,
          event_total_for_participant: eventData.event_total_points,
          event_point_summary: eventPointSummary,
          stages,
          is_v2_format: true,
        },
      ],
    };
  }

  /**
   * Loads component summary for a category using v2 API (with ranking list resolution).
   */
  loadCategoryBreakdown(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): void {
    if (event.event_id === null) {
      return;
    }

    const eventId = event.event_id;
    const cacheKey = this.getCategoryCacheKey(eventId, category.category_id);
    if (this.categorySummaryCache.has(cacheKey)) {
      return;
    }

    this.current_selected_category_id = category.category_id;
    this.selectedBreakdownEventId = event.event_id;
    this.loadingCategoryKey = cacheKey;
    this.stageLoading = true;
    this.cdr.detectChanges();

    this.resolveRankingListIdForCategory(event, category)
      .pipe(
        switchMap((rankingListId) => {
          if (!rankingListId) {
            return of(null);
          }

          this.current_ranking_list_id = [rankingListId];
          this.applyBreakdownContext(event, category, rankingListId);

          return this.leaderboardService
            .getComponentSummaryByEventStageV2(
              rankingListId,
              this.current_participant_id,
              eventId,
            )
            .pipe(
              map((response) =>
                this.mapV2ResponseToCategorySummaryItem(
                  response,
                  eventId,
                  category,
                ),
              ),
            );
        }),
      )
      .subscribe({
        next: (summaryItem) => {
          if (summaryItem) {
            this.categorySummaryCache.set(cacheKey, [summaryItem]);
          }
          this.loadingCategoryKey = null;
          this.stageLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingCategoryKey = null;
          this.stageLoading = false;
          this.snackBar.open('Failed to fetch component summary', 'Close', {
            duration: 3000,
            panelClass: ['error'],
          });
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Builds Figma-style point rows for award-standing categories.
   */
  getAwardStandingPointRows(summaryItem: CategorySummaryItem): PointBreakdownRow[] {
    const eventBreakdown = summaryItem.events_breakdown?.[0] as
      | { event_point_summary?: Record<string, number> }
      | undefined;
    const summary = eventBreakdown?.event_point_summary;
    if (!summary) {
      return [];
    }

    const rowDefinitions: Array<{ key: string; translationKey: string }> = [
      { key: 'position_points', translationKey: 'based_on_position' },
      { key: 'victory_points', translationKey: 'victory_points' },
      { key: 'bonus_points', translationKey: 'bonus_points' },
      { key: 'penalty_points', translationKey: 'penalty_points' },
    ];

    return rowDefinitions.reduce<PointBreakdownRow[]>((rows, { key, translationKey }) => {
      const rawValue = summary[key];
      if (rawValue === null || rawValue === undefined || rawValue === 0) {
        return rows;
      }
      rows.push({
        translationKey,
        value: Math.abs(rawValue),
        tournamentWeight: null,
        clickable: key === 'victory_points',
        eventBreakdown: eventBreakdown,
      });
      return rows;
    }, []);
  }

  /**
   * Builds aggregated Figma-style point rows for stage-based categories.
   */
  getStagesPointRows(summaryItem: CategorySummaryItem): PointBreakdownRow[] {
    const eventBreakdown = summaryItem.events_breakdown?.[0];
    if (!eventBreakdown) {
      return [];
    }

    const headingToKey: Record<string, string> = {
      'Victory Points': 'victory_points',
      'Bonus Points': 'bonus_points',
      'Based on Position': 'based_on_position',
      'Penalty Points': 'penalty_points',
    };

    return this.aggregateComponentPoints(eventBreakdown).map((component) => ({
      translationKey: headingToKey[component.heading] ?? component.heading,
      value: component.points,
      tournamentWeight: this.extractTournamentWeight(eventBreakdown),
      clickable: component.heading === 'Victory Points',
      eventBreakdown,
    }));
  }

  /**
   * Resolves point rows for a loaded category summary based on config_type.
   */
  getCategoryPointRows(
    summaryItem: CategorySummaryItem,
    configType: string | null,
  ): PointBreakdownRow[] {
    if (this.isAwardStandingConfig(configType)) {
      return this.getAwardStandingPointRows(summaryItem);
    }
    return this.getStagesPointRows(summaryItem);
  }

  /**
   * Reads tournament weight multiplier from the first stage when available.
   */
  private extractTournamentWeight(eventBreakdown: object): string | null {
    const stages = (eventBreakdown as Record<string, unknown>)['stages'];
    if (!Array.isArray(stages) || stages.length === 0) {
      return null;
    }
    const firstStage = stages[0] as { tournament_weight?: number | string };
    if (firstStage.tournament_weight === null || firstStage.tournament_weight === undefined) {
      return null;
    }
    return `${firstStage.tournament_weight}x`;
  }

  /**
   * Handles nested category accordion open/close and triggers summary fetch on open.
   */
  onCategoryAccordionIndexChange(
    index: AccordionIndex,
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): void {
    const activeIndex = Array.isArray(index) ? index[0] : index;

    if (activeIndex === 0) {
      if (event.event_id === null) {
        return;
      }
      const key = this.getAccordionKey(event.event_id, category.category_id);
      this.openAccordionTabs.clear();
      this.openAccordionTabs.add(key);
      this.loadCategoryBreakdown(event, category);
      this.cdr.detectChanges();
      return;
    }

    if (event.event_id === null) {
      return;
    }

    const key = this.getAccordionKey(event.event_id, category.category_id);
    this.openAccordionTabs.delete(key);
    this.cdr.detectChanges();
  }

  /**
   * Returns first cached summary item for category drill-down clicks.
   */
  getFirstCategorySummaryItem(
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
  ): CategorySummaryItem | null {
    const items = this.getCategorySummaryItems(event.event_id, category.category_id);
    return items.length > 0 ? items[0] : null;
  }

  /**
   * Handles point row click inside expanded category accordion.
   */
  onExpandedCategoryPointRowClick(
    clickEvent: Event,
    event: EventPerformance,
    category: EventCategoryBreakdownItem,
    row: PointBreakdownRow,
  ): void {
    const summaryItem = this.getFirstCategorySummaryItem(event, category);
    if (!summaryItem) {
      return;
    }
    this.onCategoryPointRowClick(clickEvent, row, summaryItem);
  }

  /**
   * Opens match history when a clickable victory-points row is selected.
   */
  onCategoryPointRowClick(
    clickEvent: Event,
    row: PointBreakdownRow,
    summaryItem: CategorySummaryItem,
  ): void {
    if (!row.clickable) {
      return;
    }

    const eventBreakdown = summaryItem.events_breakdown?.[0] as
      | {
          stages?: Array<{
            stage_id: number;
            stage_name: string;
            matches_breakdown?: unknown[];
          }>;
          event_name?: string;
        }
      | undefined;

    if (!eventBreakdown?.stages) {
      return;
    }

    const allMatches: unknown[] = [];
    eventBreakdown.stages.forEach((stage) => {
      if (Array.isArray(stage.matches_breakdown)) {
        allMatches.push(...stage.matches_breakdown);
      }
    });

    const firstStage = eventBreakdown.stages[0];
    this.checkMatchHistory(
      clickEvent,
      firstStage?.stage_id,
      allMatches,
      eventBreakdown.event_name ?? 'Match History',
    );
  }

  getRankingListCategoryMapEntries(event: EventPerformance): Array<{ key: string; value: any }> {
    if (!event?.ranking_list_category_map) {
      return [];
    }

    return Object.entries(event.ranking_list_category_map).map(([key, value]) => ({
      key,
      value,
    }));
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedBreakdownPoints']) {
      this.applySelectedBreakdownPoints();
    }

    if (
      (changes['participantId'] || changes['categoryId']) &&
      this.participantId &&
      this.categoryId
    ) {
      this.leaderboardMode = this.leaderboardService.getCurrentLeaderboardMode();
      if (this.leaderboardMode === 'Ranking') {

        this.fetchEventPerformance();
      }
      else {
        this.fetchCombinedEventPerformance();
      }
    }
  }


  fetchCombinedEventPerformance() {
    this.isLoading = true;
    this.combinedEvents = [];
    this.activeCombinedEventIndex = null;
    this.combined_matches_list = [];
    this.combinedCurrentEventName = '';
    this.combinedCurrentEvent = null;
    this.combinedCurrentCategoryName = '';
    this.showMatchesView = false;
    this.matchesViewMode = 'RankingAndRating';
    this.applySelectedBreakdownPoints();
    this.cdr.detectChanges();

    this.leaderboardService
      .getCombinedEventBreakdown(this.participantId!, this.categoryId!)
      .subscribe({
        next: (response) => {
          this.participantName =
            response.player_name || this.currentParticipantName || 'Unknown Player';
          this.combinedEvents = Array.isArray(response.events)
            ? response.events
            : [];
          this.combinedElloPoints =
            this.selectedBreakdownPoints?.eloPoints ??
            response.overall_rating_initial ??
            null;
          this.combinedBonusPoints =
            this.selectedBreakdownPoints?.bonusPoints ??
            response.overall_ranking_points ??
            null;
          this.combinedOverallTotalPoints =
            this.selectedBreakdownPoints?.totalPoints ??
            response.overall_total_points ??
            null;
          this.isLoading = false;
          this.activeCombinedEventIndex =
            this.combinedEvents.length > 0 ? 0 : null;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to fetch combined breakdown:', error);
          this.isLoading = false;
          this.combinedEvents = [];
          this.activeCombinedEventIndex = null;
          this.combinedElloPoints = null;
          this.combinedBonusPoints = null;
          this.combinedOverallTotalPoints = null;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  openCombinedCategoryMatches(
    event: CombinedEventBreakdownEvent,
    category: CombinedEventBreakdownCategory,
  ): void {
    this.matchesViewMode = 'RankingAndRating';
    this.showMatchesView = true;
    this.combined_matches_list = [];
    this.combinedCurrentEventName = event.event_name;
    this.combinedCurrentEvent = event;
    this.combinedCurrentCategoryName = category.category_name;
    this.currentStageName = category.category_name || 'Match History';
    this.combinedTotalPoints = null;
    this.combinedMatchEventDate = event.event_date ?? null;
    this.combinedMatchRatingInitial = event.rating_initial ?? null;
    this.combinedMatchRatingDelta = event.rating_delta ?? null;
    this.combinedMatchRatingFinal = event.rating_final ?? null;
    this.combinedMatchBonusPoints = event.bonus_points ?? null;
    this.cdr.detectChanges();

    this.leaderboardService
      .getEventMatchBreakdown(
        this.participantId!,
        event.event_id,
        category.category_id,
      )
      .subscribe({
        next: (response: EventMatchBreakdownResponse) => {
          this.combined_matches_list = Array.isArray(response.matches)
            ? (response.matches as unknown as CombinedEventBreakdownMatch[])
            : [];

          this.combinedTotalPoints = response.total_points ?? null;
          this.combinedMatchEventDate = response.event_date ?? this.combinedMatchEventDate;
          this.combinedMatchRatingInitial = response.rating_initial ?? this.combinedMatchRatingInitial;
          this.combinedMatchRatingDelta = response.rating_change ?? this.combinedMatchRatingDelta;
          this.combinedMatchRatingFinal = response.rating_final ?? this.combinedMatchRatingFinal;
          this.combinedMatchBonusPoints = response.bonus_points ?? this.combinedMatchBonusPoints;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to fetch event match breakdown:', error);
          this.combined_matches_list = [];
          this.combinedTotalPoints = null;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.cdr.detectChanges();
        },
      });
  }

  goBackToCombinedCategories(): void {
    this.showMatchesView = false;
    this.combined_matches_list = [];
    this.combinedCurrentEventName = '';
    this.combinedCurrentEvent = null;
    this.combinedCurrentCategoryName = '';
    this.currentStageName = '';
    this.combinedMatchEventDate = null;
    this.combinedMatchRatingInitial = null;
    this.combinedMatchRatingDelta = null;
    this.combinedMatchRatingFinal = null;
    this.combinedMatchBonusPoints = null;
    this.cdr.detectChanges();
  }

  getCombinedMatchDate(match: CombinedEventBreakdownMatch): string | null {
    return match?.match_date ?? null;
  }

  getPriorityType(row: any): 'top' | 'bottom' | null {
    const isRankingOnly = this.leaderboardMode === 'Ranking';
    const isCounted = isRankingOnly ? row?.prioritized_included : row?.is_counted;
    if (!isCounted) {
      return null;
    }

    const rawType = isRankingOnly ? row?.prioritized_type : row?.priority_tag;
    const type = String(rawType || '').toLowerCase();
    if (type === 'top' || type === 'bottom') {
      return type;
    }

    return null;
  }

  hasPriorityBadge(row: any): boolean {
    return this.getPriorityType(row) !== null;
  }

  /**
   * Whether the tournament row represents an externally sourced event.
   * @param row - Event performance or combined breakdown event row.
   * @returns True when `is_external` is explicitly true.
   */
  isExternalEvent(row: { is_external?: boolean } | null | undefined): boolean {
    return row?.is_external === true;
  }

  getPriorityLabel(row: any): 'Top' | 'Bottom' | '' {
    const priorityType = this.getPriorityType(row);
    if (priorityType === 'top') {
      return 'Top';
    }
    if (priorityType === 'bottom') {
      return 'Bottom';
    }
    return '';
  }

  getCombinedOpponentName(match: CombinedEventBreakdownMatch): string | null {
    return match?.opponent_name ?? null;
  }

  getCombinedStatus(match: CombinedEventBreakdownMatch): string | null {
    return match?.match_status ?? null;
  }

  getCombinedScore(match: CombinedEventBreakdownMatch): string | null {
    return match?.score ?? null;
  }

  getCombinedRatingExchange(match: CombinedEventBreakdownMatch): number | null {
    return match?.rating_exchange ?? null;
  }

  getCombinedRatingExchangeDisplay(
    match: CombinedEventBreakdownMatch,
  ): string | null {
    const ex = this.getCombinedRatingExchange(match);
    if (ex === null || ex === undefined) {
      return null;
    }
    if (
      match.rating_after !== null &&
      match.rating_after !== undefined &&
      match.rating_before !== null &&
      match.rating_before !== undefined &&
      match.rating_after > match.rating_before
    ) {
      return `+${ex}`;
    }
    return `${ex}`;
  }

  getCombinedRatingExchangeClass(
    match: CombinedEventBreakdownMatch,
  ): { [cls: string]: boolean } {
    const before = match.rating_before;
    const after = match.rating_after;
    return {
      'text-victory': after !== null && after !== undefined && before !== null && before !== undefined && after > before,
      'text-danger': after !== null && after !== undefined && before !== null && before !== undefined && after < before,
    };
  }

  onCombinedEventAccordionIndexChange(index: number | number[] | null): void {
    if (Array.isArray(index)) {
      this.activeCombinedEventIndex = index[0] ?? null;
      return;
    }
    this.activeCombinedEventIndex = index;
  }

  getEventRatingDeltaDisplay(ev: CombinedEventBreakdownEvent): string | number | null {
    const delta = ev.rating_delta;
    if (delta == null) {
      return '-';
    }
    if (delta > 0) {
      return `+${delta}`;
    }
    return delta;
  }

  getEventRatingDeltaClass(ev: CombinedEventBreakdownEvent): { [cls: string]: boolean } {
    const delta = ev.rating_delta;
    return {
      'text-victory': !!delta && delta > 0,
      'text-danger': !!delta && delta < 0,
    };
  }

  getCombinedMatchRatingDeltaDisplay(): string | number {
    if (
      this.combinedMatchRatingDelta === null ||
      this.combinedMatchRatingDelta === undefined
    ) {
      return '-';
    }
    if (this.combinedMatchRatingDelta > 0) {
      return `+${this.combinedMatchRatingDelta}`;
    }
    return this.combinedMatchRatingDelta;
  }

  getCombinedMatchRatingDeltaClass(): { [cls: string]: boolean } {
    const delta = this.combinedMatchRatingDelta;
    return {
      'text-victory': !!delta && delta > 0,
      'text-danger': !!delta && delta < 0,
    };
  }

  onCombinedMatchesSort(
    column: 'status' | 'opponent_name' | 'date' | 'score' | 'exchange',
  ): void {
    if (this.combinedMatchesSortBy === column) {
      this.combinedMatchesSortOrder =
        this.combinedMatchesSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.combinedMatchesSortBy = column;
      this.combinedMatchesSortOrder = 'asc';
    }
  }

  getCombinedMatchesSortIcon(
    column: 'status' | 'opponent_name' | 'date' | 'score' | 'exchange',
  ): string {
    if (this.combinedMatchesSortBy !== column) {
      return '↕';
    }
    return this.combinedMatchesSortOrder === 'asc' ? '↑' : '↓';
  }

  get sortedCombinedMatches(): CombinedEventBreakdownMatch[] {
    if (!Array.isArray(this.combined_matches_list)) {
      return [];
    }

    const sortBy = this.combinedMatchesSortBy;
    const sortOrder = this.combinedMatchesSortOrder;
    const factor = sortOrder === 'asc' ? 1 : -1;

    return [...this.combined_matches_list].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortBy) {
        case 'status':
          aVal = (a.match_status || '').toLowerCase();
          bVal = (b.match_status || '').toLowerCase();
          break;
        case 'opponent_name':
          aVal = (a.opponent_name || '').toLowerCase();
          bVal = (b.opponent_name || '').toLowerCase();
          break;
        case 'date':
          aVal = a.match_date ? new Date(a.match_date).getTime() : 0;
          bVal = b.match_date ? new Date(b.match_date).getTime() : 0;
          break;
        case 'score':
          aVal = (a.score || '').toLowerCase();
          bVal = (b.score || '').toLowerCase();
          break;
        case 'exchange':
          aVal = a.rating_exchange ?? 0;
          bVal = b.rating_exchange ?? 0;
          break;
      }

      if (aVal < bVal) return -1 * factor;
      if (aVal > bVal) return 1 * factor;
      return 0;
    });
  }
  private fetchEventPerformance(): void {

    this.isLoading = true;
    this.eventPerformances = [];
    this.participantName = 'Player Name';
    this.breakdownDetails = null;
    this.mappedBreakdownDetails = [];
    this.selectedBreakdownEventId = null;
    this.selectedMergedParentIndex = null;
    this.selectedExpandedCategoryId = null;
    this.categorySummaryCache.clear();
    this.loadingCategoryKey = null;
    this.isPaused = false;
    this.cdr.detectChanges();

    const seasonId =
      globalThis.window !== undefined && localStorage.getItem('seasonId')
        ? +localStorage.getItem('seasonId')!
        : null;

    this.leaderboardService
      .getEventPerformance(this.participantId!, seasonId!, this.categoryId!)
      .subscribe({
        next: (response) => {
          this.participantName = response.participant_name || 'Unknown Player';
          this.isPaused = response.is_paused || false;
          this.eventPerformances = response.event_performances;
          
          this.current_participant_id = response.participant_id || this.participantId || 0;
          
          
          this.isLoading = false;
          this.cdr.detectChanges();


          // Automatically open the first accordion and load its data
          // this.openFirstAccordion();    commented because to test other thing 
        },
        error: (error) => {
          console.error('Failed to fetch event performance:', error);
          this.participantName = 'Unknown Player';
          this.eventPerformances = [];
          this.isPaused = false;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Automatically open the first accordion and load its data
   */
  private openFirstAccordion(): void {
    // Wait for the view to update before opening the accordion
    setTimeout(() => {
      if (this.eventPerformances && this.eventPerformances.length > 0) {
        const firstEvent = this.eventPerformances[0];

        // First, open the event accordion
        this.openEventAccordions.clear();
        if (firstEvent.event_id !== null) {
          this.openEventAccordions.add(firstEvent.event_id);
        }
        this.cdr.detectChanges();

        // Then wait a bit more for the event accordion to render before opening the category accordion
        setTimeout(() => {
          const firstCategory = this.getCategoryBreakdown(firstEvent)[0];
          if (firstCategory) {
            this.onCategoryAccordionIndexChange(0, firstEvent, firstCategory);
          }
        }, 150);
      }
    }, 100);
  }

  toggleBreakdownByEventId(eventId: number): void {
    const eventObj = this.eventPerformances.find((e) => e.event_id === eventId);
    if (eventObj) {
      this.toggleBreakdown(eventObj);
    }
  }

  private fetchBreakdownDetails(eventId: number): void {
    const seasonId =
      globalThis.window !== undefined && localStorage.getItem('seasonId')
        ? +localStorage.getItem('seasonId')!
        : null;

    if (!seasonId) {
      this.isLoading = false;
      this.breakdownDetails = null;
      this.mappedBreakdownDetails = [];
      this.snackBar.open('Season not Found', 'Close', {
        duration: 3000,
        panelClass: ['error']
      });
      this.cdr.detectChanges();
      return;
    }

    this.breakdownEventLoadingId = eventId;
    this.isLoading = true;
    this.cdr.detectChanges();

    this.leaderboardService
      .getRankingLists(seasonId, this.categoryId!, eventId)
      .subscribe({
        next: (response) => {
          const rankingListId = response.items[0]?.ranking_list_id;
          if (!rankingListId) {
            this.breakdownEventLoadingId = null;
            this.isLoading = false;
            this.breakdownDetails = null;
            this.mappedBreakdownDetails = [];
            this.cdr.detectChanges();
            return;
          }

          localStorage.setItem('Ranking_list_id', rankingListId.toString());
          localStorage.setItem(
            'participant_id',
            this.participantId!.toString()
          );
          localStorage.setItem(
            'rankingManagement',
            JSON.stringify([
              {
                event_id: eventId,
                category_id: this.categoryId,
                season_id: seasonId,
              },
            ])
          );

          this.leaderboardService
            .getComponentSummaryByEventStage(
              rankingListId,
              this.participantId!,
              eventId
            )
            .subscribe({
              next: (summaryResponse) => {
                const eventBreakdown = summaryResponse.events_breakdown.find(
                  (e) => e.event_id === eventId
                );
                this.breakdownDetails =
                  eventBreakdown?.event_point_summary ?? null;
                this.mappedBreakdownDetails = eventBreakdown
                  ? this.aggregateComponentPoints(eventBreakdown)
                  : [];
                this.clearBreakdownEventLoading();
              },
              error: () => {
                this.breakdownDetails = null;
                this.mappedBreakdownDetails = [];
                this.clearBreakdownEventLoading();
              },
            });
        },
        error: (error) => {
          console.error('Failed to fetch ranking lists:', error);
          this.breakdownDetails = null;
          this.mappedBreakdownDetails = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private clearBreakdownEventLoading(): void {
    this.breakdownEventLoadingId = null;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private aggregateComponentPoints(eventBreakdown: object): ComponentSummary[] {
    const breakdown = eventBreakdown as Record<string, unknown>;
    const componentMap: { [key: string]: number } = {
      'Victory Points': 0,
      'Bonus Points': 0,
      'Based on Position': 0,
      'Penalty Points': 0,
    };

    const stages = breakdown['stages'];
    const isV2Format = breakdown['is_v2_format'] === true;

    if (Array.isArray(stages)) {
      for (const stage of stages as Record<string, unknown>[]) {
        if (isV2Format) {
          this.aggregateV2StagePoints(stage, componentMap);
          continue;
        }
        this.aggregateLegacyStagePoints(stage, componentMap);
      }
    }

    if (isV2Format) {
      const eventPointSummary = breakdown['event_point_summary'] as
        | Record<string, number>
        | undefined;
      if (eventPointSummary) {
        componentMap['Based on Position'] += eventPointSummary['position_points'] ?? 0;
      }
    }

    const eventWideComponents = breakdown['event_wide_components'] as
      | Record<string, { contributing_logs?: Array<{ points_component_value?: number; component_details?: { original_component_value_before_weight?: number } }>; value?: number }>
      | undefined;
    if (eventWideComponents && !isV2Format) {
      this.aggregateEventWideLegacyPoints(eventWideComponents, componentMap);
    }

    return Object.entries(componentMap)
      .filter(([_, points]) => points !== 0)
      .map(([heading, points]) => ({
        heading,
        points,
      }));
  }

  private aggregateV2StagePoints(
    stage: Record<string, unknown>,
    componentMap: Record<string, number>,
  ): void {
    componentMap['Victory Points'] += (stage['victory_points'] as number) ?? 0;
    componentMap['Bonus Points'] += (stage['bonus_points'] as number) ?? 0;
    componentMap['Based on Position'] += (stage['position_points'] as number) ?? 0;
    componentMap['Penalty Points'] += (stage['penalty_points'] as number) ?? 0;
  }

  private aggregateLegacyStagePoints(
    stage: Record<string, unknown>,
    componentMap: Record<string, number>,
  ): void {
    const matches = stage['matches_breakdown'] as Record<string, unknown>[] | undefined;
    if (Array.isArray(matches)) {
      for (const match of matches) {
        this.aggregateMatchBreakdownPoints(match, componentMap);
      }
    }
    this.aggregateStageLevelBonusPoints(stage, componentMap);
  }

  private aggregateMatchBreakdownPoints(
    match: Record<string, unknown>,
    componentMap: Record<string, number>,
  ): void {
    const matchPointComponents = match['match_point_components'] as
      | Record<string, { value?: number; contributing_logs?: Array<{ points_component_value?: number }> }>
      | undefined;

    let vpValue = 0;
    let adminAdjVp = 0;
    if (matchPointComponents?.['victory_points_plugin']) {
      vpValue = matchPointComponents['victory_points_plugin'].value || 0;
      adminAdjVp = matchPointComponents?.['admin_adj_victory_points_plugin']?.value || 0;
    } else if (matchPointComponents?.['walkover_forfeit_plugin']) {
      vpValue = matchPointComponents['walkover_forfeit_plugin'].value || 0;
      adminAdjVp = matchPointComponents?.['admin_adj_walkover_forfeit_plugin']?.value || 0;
    }
    componentMap['Victory Points'] += vpValue + adminAdjVp;

    const bonusPlugins = [
      'stage_knockout_win',
      'stage_knockout_lost',
      'stage_group_win',
      'stage_group_lost',
    ];
    for (const plugin of bonusPlugins) {
      const pluginData = matchPointComponents?.[plugin];
      const bpValue = this.sumContributingLogPoints(pluginData?.contributing_logs);
      const adminAdjBp = matchPointComponents?.[`admin_adj_${plugin}`]?.value || 0;
      componentMap['Bonus Points'] += bpValue + adminAdjBp;
    }
  }

  private aggregateStageLevelBonusPoints(
    stage: Record<string, unknown>,
    componentMap: Record<string, number>,
  ): void {
    const stageLevelBonus = stage['stage_level_bonus_points'] as
      | Record<string, { contributing_logs?: Array<{ points_component_value?: number; component_details?: { original_component_value_before_weight?: number } }>; value?: number }>
      | undefined;
    if (!stageLevelBonus) {
      return;
    }

    const stageBonusPlugins = [
      'stage_knockout_lost',
      'stage_knockout_participant',
      'stage_group_participant',
      'stage_group_lost',
    ];
    for (const plugin of stageBonusPlugins) {
      componentMap['Bonus Points'] += this.sumContributingLogPoints(
        stageLevelBonus[plugin]?.contributing_logs,
      );
    }

    if (stageLevelBonus['admin_adj_penalty_points']?.value) {
      componentMap['Penalty Points'] += stageLevelBonus['admin_adj_penalty_points'].value || 0;
    }

    const tournamentWeightLogs = stageLevelBonus['tournament_weight_plugin']?.contributing_logs;
    if (tournamentWeightLogs) {
      componentMap['Based on Position'] += this.sumOriginalComponentValuesBeforeWeight(
        tournamentWeightLogs,
      );
      return;
    }

    componentMap['Based on Position'] += this.sumContributingLogPoints(
      stageLevelBonus['stage_knockout_round']?.contributing_logs,
    );
    componentMap['Based on Position'] += this.sumContributingLogPoints(
      stageLevelBonus['stage_group_position']?.contributing_logs,
    );
  }

  private aggregateEventWideLegacyPoints(
    eventWideComponents: Record<string, { contributing_logs?: Array<{ points_component_value?: number; component_details?: { original_component_value_before_weight?: number } }>; value?: number }>,
    componentMap: Record<string, number>,
  ): void {
    const tournamentWeightLogs = eventWideComponents['tournament_weight_plugin']?.contributing_logs;
    if (tournamentWeightLogs) {
      componentMap['Based on Position'] += this.sumOriginalComponentValuesBeforeWeight(
        tournamentWeightLogs,
      );
    } else {
      componentMap['Based on Position'] += this.sumContributingLogPoints(
        eventWideComponents['tournament_points_plugin']?.contributing_logs,
      );
    }

    if (eventWideComponents['admin_adj_penalty_points']?.value) {
      componentMap['Penalty Points'] += eventWideComponents['admin_adj_penalty_points'].value || 0;
    }
  }

  private sumContributingLogPoints(
    logs?: Array<{ points_component_value?: number }>,
  ): number {
    if (!logs) {
      return 0;
    }
    return logs.reduce((sum, log) => sum + (log.points_component_value || 0), 0);
  }

  private sumOriginalComponentValuesBeforeWeight(
    logs?: Array<{ component_details?: { original_component_value_before_weight?: number } }>,
  ): number {
    if (!logs) {
      return 0;
    }
    return logs.reduce(
      (sum, log) => sum + (log.component_details?.original_component_value_before_weight || 0),
      0,
    );
  }

  onPointClick(key: string) {
    this.selectedPointKey = key;
    this.isEditSidebarOpen = true;
    this.cdr.detectChanges();
  }

  closeEditSidebar() {
    this.isEditSidebarOpen = false;
    this.selectedPointKey = null;
    this.cdr.detectChanges();
  }

  toggleExpiredPoints() {
    this.showExpiredPoints = !this.showExpiredPoints;
    this.cdr.detectChanges();
  }

  onClose() {
    this.showExpiredPoints = false;
    this.isEditSidebarOpen = false;
    this.selectedPointKey = null;
    this.showPausePopup = false;
    this.openEventAccordions.clear();
    this.combinedElloPoints = null;
    this.combinedBonusPoints = null;
    this.combinedOverallTotalPoints = null;
    this.panelClose.emit();
  }

  private applySelectedBreakdownPoints(): void {
    this.combinedElloPoints = this.selectedBreakdownPoints?.eloPoints ?? null;
    this.combinedBonusPoints = this.selectedBreakdownPoints?.bonusPoints ?? null;
    this.combinedOverallTotalPoints =
      this.selectedBreakdownPoints?.totalPoints ?? null;
  }

  /**
   * Check if an event accordion is open
   */
  isEventAccordionOpen(eventId: number): boolean {
    return this.openEventAccordions.has(eventId);
  }

  /**
   * Toggle event accordion open/close state
   * Only one accordion can be open at a time
   */
  toggleEventAccordion(event: any, eventId: number): void {


    this.isManuallyToggling = true;

    const isCurrentlyOpen = this.openEventAccordions.has(eventId);

    // First, close ALL accordions to ensure clean state
    this.openEventAccordions.clear();
    this.openAccordionTabs.clear();

    if (isCurrentlyOpen) {
      // If it was open, clear the data since we're closing it
      this.breakdownDetailsList = [];
      this.categorySummaryCache.clear();
    } else {
      // Only open the clicked accordion if it wasn't already open
      this.openEventAccordions.add(eventId);
    }

    // Force change detection to update the UI immediately
    this.cdr.detectChanges();

    // Reset flag after a short delay to allow state to settle
    setTimeout(() => {
      this.isManuallyToggling = false;
    }, 200);
  }

  /**
   * Handle event accordion index change to keep state in sync
   * Only one accordion can be open at a time
   */
  onEventAccordionIndexChange(index: any): void {
    const activeIndex = Array.isArray(index) ? index[0] : index;

    if (typeof activeIndex === 'number' && activeIndex >= 0) {
      // Accordion is opening - close all others
      this.activeEventIndex = activeIndex;
      this.openEventAccordions.clear();
      this.openAccordionTabs.clear();

      const eventObj = this.eventPerformances[activeIndex];
      const eventId = eventObj?.event_id;

      if (eventId) {
        this.openEventAccordions.add(eventId);
      }

      this.cdr.detectChanges();
    } else if (activeIndex === null || activeIndex === -1) {
      // Accordion is closing
      this.activeEventIndex = null;
      this.openEventAccordions.clear();
      this.openAccordionTabs.clear();
      this.breakdownDetailsList = [];
      this.categorySummaryCache.clear();
      this.loadingCategoryKey = null;
      this.cdr.detectChanges();
    }
  }

  /**
   * Allow clicking the custom header to toggle the accordion, keeping the single-open logic.
   */
  handleEventHeaderClick(event: Event, index: number): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.activeEventIndex === index) {
      // Close current
      this.onEventAccordionIndexChange(null);
    } else {
      // Open clicked
      this.onEventAccordionIndexChange(index);
    }
  }


  handleCloseEventAccordion(event: any): void {

    this.onEventAccordionIndexChange(null);
  }

  onSaveChanges(event: any) {

    if (this.selectedBreakdownEventId && event.events) {
      const eventBreakdown = event.events.find(
        (e: any) => e.event_id === this.selectedBreakdownEventId
      );
      if (eventBreakdown) {
        const stage = eventBreakdown.stages.reduce(
          (acc: { [key: string]: number }, s: any) => {
            acc['Total Points'] = (acc['Total Points'] || 0) + s.totalPoints;
            return acc;
          },
          { ...this.breakdownDetails }
        );
        this.breakdownDetails = stage;
        this.mappedBreakdownDetails = this.aggregateComponentPoints(eventBreakdown);
        this.cdr.detectChanges();
      }
    }
    this.closeEditSidebar();
  }

  onUndoChanges() {

    this.cdr.detectChanges();
  }

  onVictoryPointChanges(event: any) {

    if (this.selectedBreakdownEventId && event.stage_id) {
      const stage = event.matches.reduce(
        (acc: { [key: string]: number }, match: any) => {
          if (match.vp)
            acc['Victory Points'] =
              (acc['Victory Points'] || 0) + Number.parseFloat(match.vp);
          if (match.bp)
            acc['Bonus Points'] =
              (acc['Bonus Points'] || 0) + Number.parseFloat(match.bp);
          return acc;
        },
        { ...this.breakdownDetails }
      );
      this.breakdownDetails = stage;
      this.mappedBreakdownDetails = this.aggregateComponentPoints({
        event_id: this.selectedBreakdownEventId,
        stages: [
          { stage_id: event.stage_id, matches_breakdown: event.matches },
        ],
      });
      this.cdr.detectChanges();
    }
  }

  togglePausePopup() {
    this.showPausePopup = true;
    this.cdr.detectChanges();
  }

  closePausePopup() {
    this.showPausePopup = false;
    this.cdr.detectChanges();
  }

  confirmPause() {
    if (!this.participantId) return;

    const today = new Date().toISOString().split('T')[0];
    const userId =
      globalThis.window !== undefined && localStorage.getItem('rl')
        ? localStorage.getItem('rl')!
        : '';

    this.leaderboardService
      .togglePause(this.participantId, today, 'pause', userId)
      .subscribe({
        next: () => {
          this.isPaused = !this.isPaused;
          this.closePausePopup();
          this.pauseStatusChanged.emit();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error toggling pause status:', error);
          this.closePausePopup();
          this.cdr.detectChanges();
        },
      });
  }


  get_component_summary() {

    this.stageLoading = true;

    // Convert participantId to string as required by the service
    this.editSidebarService.getBulkComponnentSummary({ ranking_list_ids: this.current_ranking_list_id, participant_id: this.current_participant_id }).subscribe({
      next: (response: any) => {
        // this.breakdownDetailsList=[]
        this.stageLoading = false;

        const data = response.data;

        this.breakdownDetailsList = data

      },
      error: (error: any) => {
        this.stageLoading = false;
        console.error('Error fetching component summary:', error);
        this.snackBar.open('Failed to fetch component summary', 'Close', {
          duration: 3000,
          panelClass: ['error']
        });
      },
    });
  }


  view_component_summary(
    data: EventCategoryBreakdownItem,
    ranking_list_id?: string,
    event?: EventPerformance,
  ): void {
    if (!event || !ranking_list_id) {
      return;
    }

    this.loadCategoryBreakdown(event, {
      ...data,
      ranking_list_id,
    });
  }

  onAccordionOpen(categoryId: number): void {
    this.openAccordionTabs.add(categoryId);
    this.cdr.detectChanges();
  }

  onAccordionClose(categoryId: number): void {
    this.openAccordionTabs.delete(categoryId);
    this.cdr.detectChanges();
  }

  /**
   * Create a unique identifier for accordion using event_id and category_id.
   */
  getAccordionKey(eventId: number, categoryId: number): string {
    if (!eventId || !categoryId) {
      return '';
    }
    return `${eventId}_${categoryId}`;
  }

  isAccordionOpen(eventId: number, categoryId: number): boolean {
    if (!eventId || !categoryId) {
      return false;
    }
    return this.openAccordionTabs.has(this.getAccordionKey(eventId, categoryId));
  }

  onAccordionIndexChange(
    index: number | number[] | null,
    data: EventCategoryBreakdownItem,
    ranking_list_id?: string,
    eventId?: number,
    categoryId?: number,
    event?: EventPerformance,
  ): void {
    const activeIndex = Array.isArray(index) ? index[0] : index;

    if (activeIndex === 0 && eventId && categoryId && event) {
      const key = this.getAccordionKey(eventId, categoryId);
      this.openAccordionTabs.clear();
      this.openAccordionTabs.add(key);
      this.onCategoryAccordionIndexChange(0, event, data);
      this.cdr.detectChanges();
      return;
    }

    if (eventId && categoryId) {
      const key = this.getAccordionKey(eventId, categoryId);
      this.openAccordionTabs.delete(key);
    }
    this.cdr.detectChanges();
  }

  getStagePointSummaryItems(eventPointSummary: any): Array<{ heading: string, value: number }> {

    if (!eventPointSummary || typeof eventPointSummary !== 'object') {
      return [];
    }

    // Mapping for keys to display headings
    const keyToHeadingMap: { [key: string]: string } = {
      'victory_points_plugin': 'Victory points',
      'stage_knockout_win': 'Bonus points',
      'stage_knockout_lost': 'Bonus points',
      'stage_group_win': 'Bonus points',
      'stage_group_lost': 'Bonus points',
      'stage_group_participant': 'Participant points',
      'admin_adj_penalty_points': 'Penality points',
    };

    // Aggregate values by heading
    const aggregatedMap: { [heading: string]: number } = {};

    // Process each key in event_point_summary
    Object.keys(eventPointSummary).forEach(key => {
      const value = eventPointSummary[key];
      // Only include non-zero values
      if (value !== null && value !== undefined && value !== 0) {
        const heading = keyToHeadingMap[key] || key;
        aggregatedMap[heading] = (aggregatedMap[heading] || 0) + value;
      }
    });

    // Convert aggregated map to array
    return Object.keys(aggregatedMap).map(heading => ({
      heading: heading,
      value: aggregatedMap[heading]
    }));
  }

  getEventPointSummaryItems(eventPointSummary: any): Array<{ heading: string, value: number }> {

    if (!eventPointSummary || typeof eventPointSummary !== 'object') {
      return [];
    }

    // Mapping for keys to display headings
    const keyToHeadingMap: { [key: string]: string } = {
      'victory_points_plugin': 'Victory points',
      'tournament_points_plugin': 'Based on Position',
      'stage_knockout_win': 'Bonus points',
      'stage_knockout_lost': 'Bonus points',
      'stage_group_win': 'Bonus points',
      'stage_group_lost': 'Bonus points',
      'stage_group_participant': 'Participant points',
      'admin_adj_penalty_points': 'Penality points',
    };

    // Aggregate values by heading
    const aggregatedMap: { [heading: string]: number } = {};

    // Process each key in event_point_summary
    Object.keys(eventPointSummary).forEach(key => {
      const value = eventPointSummary[key];
      // Only include non-zero values
      if (value !== null && value !== undefined && value !== 0) {
        const heading = keyToHeadingMap[key] || key;
        aggregatedMap[heading] = (aggregatedMap[heading] || 0) + value;
      }
    });

    // Convert aggregated map to array
    return Object.keys(aggregatedMap).map(heading => ({
      heading: heading,
      value: aggregatedMap[heading]
    }));
  }


  checkMatchHistory(event: Event, stage_id: any, matches_list: any, stage_name?: string) {
    event.stopPropagation(); // Prevent event from bubbling up
    event.preventDefault(); // Prevent default behavior
    this.matches_list = matches_list || [];

    this.showMatchesView = true;
    this.matchesViewMode = 'Ranking';
    this.currentStageName = stage_name || 'Match History';
    this.cdr.detectChanges();
  }

  onPointValueClick(event: Event, pointItem: any, stage_id: any, matches_list: any, stage_name?: string) {
    // Only allow click for Victory points
    if (pointItem.heading === 'Victory points') {
      this.checkMatchHistory(event, stage_id, matches_list, stage_name);
    }
  }

  onEventPointValueClick(event: Event, pointItem: any, eventItem: any, stage_id: any, stage_name?: string) {
    // Only allow click for Victory points
    if (pointItem.heading === 'Victory points') {
      // Collect all matches from all stages of this event
      const allMatches: any[] = [];
      if (eventItem?.stages && Array.isArray(eventItem.stages)) {
        eventItem.stages.forEach((stage: any) => {
          if (stage?.matches_breakdown && Array.isArray(stage.matches_breakdown)) {
            allMatches.push(...stage.matches_breakdown);
          }
        });
      }
      const eventStageName = stage_name || eventItem?.event_name || 'Match History';
      this.checkMatchHistory(event, stage_id, allMatches, eventStageName);
    }
  }

  goBackToBreakdown(): void {
    this.showMatchesView = false;
    this.matches_list = [];
    this.currentStageName = '';
    this.matchesViewMode = 'Ranking';
    this.cdr.detectChanges();
  }

  /**
   * Get Victory Points (VP) from match_point_components
   * Returns the value from victory_points_plugin if present, otherwise null
   */
  getVictoryPoints(match: any): number | null {
    if (!match?.match_point_components?.victory_points_plugin) {
      return null;
    }
    return match.match_point_components.victory_points_plugin.value ?? null;
  }

  /**
   * Get Bonus Points (BP) from match_point_components
   * Returns the value from stage_knockout_win if present, otherwise null
   */
  getBonusPoints(match: any): number | null {
    if (!match?.match_point_components?.stage_knockout_win) {
      return null;
    }
    return match.match_point_components.stage_knockout_win.value ?? null;
  }

  /**
   * Get match position, returns "N/A" if not found
   */
  getMatchPosition(match: any): string {
    const position = match?.match_position;
    if (position !== null && position !== undefined && position !== '') {
      return String(position);
    }
    return 'N/A';
  }

  /**
   * Get match date, returns null if not present
   */
  getMatchDate(match: any): string | null {
    return match?.match_date ?? null;
  }

  /**
   * Calculate total points (VP + BP) from all matches
   */
  getTotalPoints(): number {
    if (!this.matches_list || this.matches_list.length === 0) {
      return 0;
    }

    let total = 0;
    this.matches_list.forEach((match: any) => {
      const vp = this.getVictoryPoints(match);
      const bp = this.getBonusPoints(match);
      total += (vp ?? 0) + (bp ?? 0);
    });

    return total;
  }



  trackById(index: number, item: unknown): string | number {
    if (item == null || typeof item !== 'object') {
      return index;
    }
    if ('event_id' in item) {
      const eventId = (item as { event_id?: number }).event_id;
      if (eventId != null) {
        return eventId;
      }
    }
    if ('id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) {
        return id;
      }
    }
    return index;
  }
}